import { GridApi, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidV4 } from 'uuid';
import { ZodArray, ZodType } from 'zod';

import { useGridValidator, useValidationTrigger } from '../components/IntakeDataGrid/IntakeDataGrid.hooks';
import { IntakeSupplierSource } from '../steps/IntakeRequestSupplierSelectionStep/components/IntakeSupplierSource';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  DeleteActionCell,
  editCellFactory,
  gridColDefFactory,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { LocationLink } from '~/components/LocationLink/LocationLink.tsx';
import {
  SupplierContactDataOptional,
  supplierContactDefault,
} from '~/routes/IntakeDetail/components/SupplierContactForm/SupplierContactForm.schema';

export enum IntakeRequestSupplierSelectionGridField {
  CONTACT_EMAIL = 'contact.email',
  CONTACT_LANGUAGE = 'contact.language',
  CONTACT_PERSON = 'contact.person',
  LOCATION = 'location',
  DELETE_ACTION = 'deleteAction',
  INTERNAL_ID = 'internalId',
  SOURCE = 'source',
}

interface SupplierSelectionDataGridProps {
  onRowDelete?: (id: GridRowId) => void;
  includeSource?: boolean;
}
export const useSupplierSelectionGridConfig: UseDataGridConfig<
  SupplierContactDataOptional,
  SupplierSelectionDataGridProps
> = (props) => {
  const { onRowDelete, includeSource = false } = props ?? {};
  const { t } = useTranslation(I18nNamespace.intake);
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'SupplierSelectionGrid.column',
  });
  const { t: tLanguages } = useTranslation(I18nNamespace.languages);

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    return {
      columns: [
        createColumn({
          field: IntakeRequestSupplierSelectionGridField.INTERNAL_ID,
          minWidth: 250,
          valueGetter: (_value, row) => {
            return row.supplier.internalId;
          },
          renderCell: cellFactory(CuriosityValueCell, ({ value }) => ({
            ns: CuriosityNamespaceType.SUPPLIER,
            fallback: value,
          })),
        }),

        includeSource
          ? createColumn({
              field: IntakeRequestSupplierSelectionGridField.SOURCE,
              minWidth: 120,
              valueGetter: (_value, row) => {
                return row.supplier;
              },
              renderCell: cellFactory(IntakeSupplierSource),
            })
          : undefined,

        createColumn({
          field: IntakeRequestSupplierSelectionGridField.LOCATION,
          valueGetter: (_value, row) => {
            return row.supplier.location;
          },
          renderCell: cellFactory(LocationLink),
          minWidth: 400,
        }),
        createColumn({
          field: IntakeRequestSupplierSelectionGridField.CONTACT_PERSON,
          minWidth: 200,
          valueGetter: (_value, row) => {
            return [row.supplier.contact?.firstName, row.supplier.contact?.lastName].filter(Boolean).join(' ');
          },
        }),
        createColumn({
          field: IntakeRequestSupplierSelectionGridField.CONTACT_EMAIL,
          minWidth: 250,
          valueGetter: (_value, row) => {
            return row.supplier.contact?.email;
          },
        }),
        createColumn({
          field: IntakeRequestSupplierSelectionGridField.CONTACT_LANGUAGE,
          minWidth: 150,
          valueGetter: (_value, row) => {
            return row.supplier.contact?.language;
          },
          valueFormatter: (value) => {
            return tLanguages(String(value).toLowerCase());
          },
        }),
        createColumn({
          field: IntakeRequestSupplierSelectionGridField.DELETE_ACTION,
          width: 60,
          renderHeader: () => null,
          align: 'center',
          renderCell: editCellFactory(DeleteActionCell, {
            onClick: onRowDelete,
          }),
        }),
      ].filter((column) => column !== undefined),
    };
  }, [t, tColumn, tLanguages, includeSource, onRowDelete]);
};

export const useSupplierGridHandlers = <T extends Record<string, any>>(
  apiRef: MutableRefObject<GridApi>,
  gridSchema: ZodArray<ZodType<T>>,
) => {
  const { triggeredValidations, shouldValidate } = useValidationTrigger();

  const { getRawRow, getRawRows } = useGridValidator({
    apiRef,
    schema: gridSchema,
  });

  const handleAddRow = useCallback(() => {
    const id = uuidV4();
    const newSupplier = {
      id,
      supplier: supplierContactDefault,
    };

    apiRef.current.updateRows([{ ...newSupplier, action: 'insert' }]);
    apiRef.current.selectRow(id, true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowDelete = useCallback((id: any) => {
    apiRef.current.updateRows([{ id, _action: 'delete' }]);
    apiRef.current.selectRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseDrawer = useCallback(() => {
    if (apiRef.current && apiRef.current.getSelectedRows) {
      const ids = Array.from(apiRef.current.getSelectedRows().keys());
      apiRef.current.selectRows(ids, false);
      shouldValidate(ids[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const currentSelectedRowId = useMemo(() => rowSelectionModel[0], [rowSelectionModel]);

  const handleSupplierChange = useCallback(
    (supplier: SupplierContactDataOptional) => {
      const id = rowSelectionModel[0];

      // Enforce validation only, if previous action wasn't the initial insert (handleAddRow)
      // Validation is enforced on closing the drawer latest (handleCloseDrawer)
      // if any update takes place (action is update), we want to validate the row
      const lastGridRowAction = apiRef?.current.getRowModels().get(id)?.action;
      if (lastGridRowAction !== 'insert') {
        shouldValidate(id);
      }
      apiRef?.current.updateRows([{ ...supplier, id, action: 'update' }]);
    },
    [apiRef, rowSelectionModel, shouldValidate],
  );

  return {
    currentSelectedRowId,
    handleAddRow,
    handleRowDelete,
    handleCloseDrawer,
    handleSupplierChange,
    hasSupplier: getRawRows().length > 0,
    selectedSupplier: getRawRow(currentSelectedRowId),
    setRowSelectionModel,
    validate: triggeredValidations[currentSelectedRowId],
  };
};
